/**
 * Log parsing and grouping utilities for LogViewer
 */

import type { LogEvent } from '@/types/dashboard';
import type { RequestMetrics, RequestGroup } from './types';
import { UNGROUPED_REQUEST_ID } from './types';

// =============================================================================
// System Log Detection
// =============================================================================

/** Prefixes that identify Lambda system logs */
const SYSTEM_LOG_PREFIXES = [
  'START RequestId:',
  'END RequestId:',
  'REPORT RequestId:',
  'INIT_START',
] as const;

/**
 * Check if a log message is a Lambda system log (START, END, INIT_START, REPORT)
 *
 * @param message - Log message content
 * @returns true if this is a system-generated Lambda log
 */
export function isSystemLog(message: string): boolean {
  return SYSTEM_LOG_PREFIXES.some((prefix) => message.startsWith(prefix));
}

/**
 * Extract request ID from a system log message
 *
 * @param message - System log message (START, END, REPORT, INIT_START)
 * @returns Request ID if found, undefined otherwise
 */
export function extractRequestIdFromSystemLog(
  message: string,
): string | undefined {
  const match = message.match(/RequestId:\s*([a-f0-9-]+)/i);
  return match?.[1];
}

// =============================================================================
// REPORT Log Parsing
// =============================================================================

/** Regex patterns for extracting metrics from REPORT logs */
const METRIC_PATTERNS = {
  duration: /Duration:\s*([\d.]+)\s*ms/,
  billedDuration: /Billed Duration:\s*([\d.]+)\s*ms/,
  memorySize: /Memory Size:\s*([\d.]+)\s*MB/,
  maxMemoryUsed: /Max Memory Used:\s*([\d.]+)\s*MB/,
  initDuration: /Init Duration:\s*([\d.]+)\s*ms/,
} as const;

/**
 * Parse REPORT log to extract Lambda execution metrics
 *
 * @example
 * parseReportMetrics("REPORT RequestId: xxx Duration: 123.45 ms Billed Duration: 200 ms Memory Size: 128 MB Max Memory Used: 64 MB Init Duration: 456.78 ms")
 * // Returns: { duration: 123.45, billedDuration: 200, memorySize: 128, maxMemoryUsed: 64, initDuration: 456.78 }
 *
 * @param message - REPORT log message
 * @returns Extracted metrics (undefined values if not found)
 */
export function parseReportMetrics(message: string): RequestMetrics {
  const metrics: RequestMetrics = {};

  for (const [key, pattern] of Object.entries(METRIC_PATTERNS)) {
    const match = message.match(pattern);
    if (match) {
      metrics[key as keyof RequestMetrics] = parseFloat(match[1]);
    }
  }

  return metrics;
}

// =============================================================================
// Log Grouping
// =============================================================================

/**
 * Group logs by RequestId, filtering out system logs and extracting metrics
 *
 * This function:
 * 1. Groups user logs (INFO, WARN, ERROR) by their requestId
 * 2. Extracts execution metrics from REPORT logs
 * 3. Identifies cold starts from INIT_START logs
 * 4. Filters out system logs (START, END, REPORT, INIT_START) from the output
 * 5. Creates an "ungrouped" category for logs without requestId
 *
 * @param events - Array of log events from the API
 * @returns Array of request groups, sorted by start time (newest first)
 */
export function groupLogsByRequestId(events: LogEvent[]): RequestGroup[] {
  const groupMap = new Map<string, RequestGroup>();
  const ungroupedLogs: LogEvent[] = [];

  // Track cold starts separately since INIT_START comes before user logs
  const coldStartRequests = new Set<string>();

  // Track metrics from REPORT logs (may come before or after user logs)
  const metricsMap = new Map<string, RequestMetrics>();

  // First pass: identify cold starts and collect metrics
  for (const event of events) {
    const message = event.message;

    // Track cold starts
    if (message.startsWith('INIT_START')) {
      // INIT_START doesn't have requestId in our parsed format,
      // but we can get it from the next START log or from event.requestId
      if (event.requestId) {
        coldStartRequests.add(event.requestId);
      }
      continue;
    }

    // Collect metrics from REPORT logs
    if (message.startsWith('REPORT RequestId:')) {
      const requestId =
        event.requestId || extractRequestIdFromSystemLog(message);
      if (requestId) {
        metricsMap.set(requestId, parseReportMetrics(message));
        // REPORT logs have Init Duration if it was a cold start
        if (message.includes('Init Duration:')) {
          coldStartRequests.add(requestId);
        }
      }
      continue;
    }

    // Skip other system logs
    if (isSystemLog(message)) {
      continue;
    }

    // No requestId - add to ungrouped
    if (!event.requestId) {
      ungroupedLogs.push(event);
      continue;
    }

    // Get or create group for this requestId
    const requestId = event.requestId;
    if (!groupMap.has(requestId)) {
      groupMap.set(requestId, createEmptyGroup(requestId, event.timestamp));
    }

    const group = groupMap.get(requestId)!;
    group.logs.push(event);

    // Update time range using numeric comparison (more efficient)
    const eventTime = new Date(event.timestamp).getTime();
    const startTime = new Date(group.startTime).getTime();
    const endTime = new Date(group.endTime).getTime();

    if (eventTime < startTime) {
      group.startTime = event.timestamp;
    }
    if (eventTime > endTime) {
      group.endTime = event.timestamp;
    }

    // Track error/warning presence
    if (event.level === 'ERROR') {
      group.hasError = true;
    }
    if (event.level === 'WARN') {
      group.hasWarning = true;
    }
  }

  // Second pass: apply metrics and cold start flags to groups
  for (const [requestId, group] of groupMap) {
    if (metricsMap.has(requestId)) {
      group.metrics = metricsMap.get(requestId)!;
    }
    if (coldStartRequests.has(requestId)) {
      group.isColdStart = true;
    }
  }

  // Convert map to array and sort by start time (newest first)
  const groups = Array.from(groupMap.values())
    .filter((group) => group.logs.length > 0)
    .sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
    );

  // Add ungrouped logs as a synthetic group at the end
  if (ungroupedLogs.length > 0) {
    groups.push(createUngroupedGroup(ungroupedLogs));
  }

  return groups;
}

/**
 * Create an empty request group with default values
 */
function createEmptyGroup(requestId: string, timestamp: string): RequestGroup {
  return {
    requestId,
    logs: [],
    startTime: timestamp,
    endTime: timestamp,
    metrics: {},
    hasError: false,
    hasWarning: false,
    isColdStart: false,
  };
}

/**
 * Create a synthetic group for logs without requestId
 */
function createUngroupedGroup(logs: LogEvent[]): RequestGroup {
  return {
    requestId: UNGROUPED_REQUEST_ID,
    logs,
    startTime: logs[0].timestamp,
    endTime: logs[logs.length - 1].timestamp,
    metrics: {},
    hasError: logs.some((l) => l.level === 'ERROR'),
    hasWarning: logs.some((l) => l.level === 'WARN'),
    isColdStart: false,
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Generate a unique key for a log event row (for React keys)
 *
 * @param event - Log event
 * @param index - Index in the list (for uniqueness)
 * @returns Unique string key
 */
export function getLogEventKey(event: LogEvent, index: number): string {
  return `${event.timestamp}-${event.logStreamName}-${index}`;
}
